import DropdownMenu from "@/components/ui/new-dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAppContext } from "@/contexts/AppContext";
import { cn } from "@/lib/utils";
import { api } from "@/trpc/react";
import { CaretDownIcon, BuildingsIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function OrganizationSelector({
  isCollapsed,
}: {
  isCollapsed: boolean;
}) {
  const { user } = useAppContext();
  const utils = api.useUtils();

  // Fetch organizations with their billing plans
  const { data: organizationsWithPlans } =
    api.organizations.getUserOrgsWithPlans.useQuery();

  const setActiveOrgMutation =
    api.organizations.setActiveOrganization.useMutation({
      onSuccess: async () => {
        // Invalidate all queries to force refetch with new organization
        await utils.invalidate();

        toast.success("Switching organization...");

        // Reload page to load new organization from saved metadata
        setTimeout(() => {
          window.location.href = "/dashboard";
        }, 500);
      },
      onError: (error) => {
        console.error("❌ Failed to switch organization:", error);
        toast.error("Failed to switch organization. Please try again.");
      },
    });

  const handleOrgSwitch = (clerkOrgId: string) => {
    if (!clerkOrgId) {
      toast.error(
        "Organization not synced with Clerk. Please contact support.",
      );
      return;
    }

    setActiveOrgMutation.mutate({ clerkOrgId });
  };

  // Check if user has at least one Pro or Enterprise organization
  const hasProOrg = organizationsWithPlans?.some((org) => org.isPro) ?? false;

  // Don't show selector if user doesn't have at least one Pro org
  if (!hasProOrg) {
    return null;
  }

  // Don't show selector if user only has one organization
  if (!organizationsWithPlans || organizationsWithPlans.length <= 1) {
    return null;
  }

  const menuItems = organizationsWithPlans.map((org) => {
    return {
      id: org.id,
      text: org.name,
      icon: <BuildingsIcon size={16} />,
      type: "button" as const,
      onClick: () => handleOrgSwitch(org.clerkInvitationOrgId!),
      isSelected: org.id === user?.organization.id,
    };
  });

  return (
    <DropdownMenu
      menuItems={menuItems}
      side={isCollapsed ? "right" : "bottom"}
      className={cn(isCollapsed ? "mt-[100px]" : "ml-5")}
    >
      <div
        className={cn(
          "flex items-center justify-between h-spacing-8 rounded-sm gap-spacing-2 px-spacing-3 transition-colors hover:bg-palette-surface-container cursor-pointer",
        )}
      >
        <div className="flex gap-spacing-3 items-center min-w-0">
          {isCollapsed ? (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <BuildingsIcon className="h-5 w-5 shrink-0" />
              </TooltipTrigger>
              <TooltipContent side="right" className="ml-2" sideOffset={8}>
                {user?.organization?.name}
              </TooltipContent>
            </Tooltip>
          ) : (
            <BuildingsIcon className="h-5 w-5 shrink-0" />
          )}
          <span
            className={cn(
              "pb-[2px] text-palette-text-primary text-sm font-medium font-sans transition-all duration-300 ease-in-out inline-block truncate",
              isCollapsed
                ? "opacity-0 scale-90 w-0"
                : "opacity-100 scale-100 w-auto",
            )}
          >
            {user?.organization?.name}
          </span>
        </div>
        <CaretDownIcon
          className="text-palette-icon-default transition-transform"
          size={16}
        />
      </div>
    </DropdownMenu>
  );
}
